import React, {ReactElement, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';
import authService from '../services/auth.service';
import {ModelUsage, ProviderDescription, UsageRun} from '../services/llmUsage.service';
import {useLlmUsage} from '../hooks/useLlmUsage';
import {formatDayTime} from '../utils/date-format';

/** Provider names as people know them; anything else is shown as the provider names itself. */
const PROVIDER_NAMES: Record<string, string> = {
    bedrock: 'Amazon Bedrock',
};

const JOB_LABELS: Record<string, string> = {
    INTEGRATED_RISK_PICTURE: 'IRP mapping',
    BULK_DESCRIPTOR_MAPPING: 'Descriptor mapping',
    COMPONENT_MAPPING_RUN: 'Component mapping',
    SAFETY_RECOMMENDATION_TAXONOMY_TAGGING: 'Taxonomy tagging',
    SAFETY_RECOMMENDATION_INDEX: 'Search index',
};

/**
 * Dollars to the cent, or to a tenth of a cent below a dollar, where most single runs sit. A cost too
 * small to show at that precision says so, rather than showing as free.
 */
export function formatCost(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'n/a';
    if (value > 0 && value < 0.0005) return '<$0.001';
    const toTenthOfACent = Math.round(value * 1000) / 1000;
    return toTenthOfACent < 1 ? `$${toTenthOfACent.toFixed(3)}` : `$${value.toFixed(2)}`;
}

export function formatTokens(value: number): string {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return String(value);
}

interface RunTotals {
    calls: number;
    promptTokens: number;
    completionTokens: number;
}

function runTotals(run: UsageRun): RunTotals {
    const totals: RunTotals = { calls: 0, promptTokens: 0, completionTokens: 0 };
    Object.values(run.llmUsage ?? {}).forEach(usage => {
        totals.calls += usage.calls ?? 0;
        totals.promptTokens += usage.promptTokens ?? 0;
        totals.completionTokens += usage.completionTokens ?? 0;
    });
    return totals;
}

/** The provider and its models in one line, leaving out what it does not say. */
export function describeProvider(provider: ProviderDescription): string {
    const name = PROVIDER_NAMES[provider.provider ?? ''] ?? provider.provider ?? 'Unknown provider';
    const parts: string[] = [];
    if (provider.fastModel) {
        parts.push(`${provider.fastModel} for mapping${provider.fastServiceTier ? ` (${provider.fastServiceTier} tier)` : ''}`);
    }
    if (provider.reasoningModel) parts.push(`${provider.reasoningModel} for reasoning`);
    if (provider.embeddingModel) parts.push(`${provider.embeddingModel} for search`);
    return `${name}${provider.region ? `, ${provider.region}` : ''}${parts.length > 0 ? `: ${parts.join(', ')}` : ''}`;
}

function subject(run: UsageRun): string {
    if (run.irpName) return run.irpName;
    if (run.irpId !== undefined) return `IRP ${run.irpId}`;
    if (run.source) return run.source;
    return '';
}

function StatCard({label, value, detail}: {label: string; value: string; detail?: string}): ReactElement {
    return (
        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h5">{value}</Typography>
            {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
        </Paper>
    );
}

function ModelUsageTable({rows}: {rows: ModelUsage[]}): ReactElement {
    if (rows.length === 0) {
        return <Typography variant="body2" color="text.secondary">No model calls since the server started.</Typography>;
    }
    return (
        <TableContainer>
            <Table size="small" aria-label="usage since server start">
                <TableHead>
                    <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Errors</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Cached</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Cost</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(row => (
                        <TableRow key={row.model}>
                            <TableCell>{row.model}</TableCell>
                            <TableCell align="right">{row.calls.toLocaleString()}</TableCell>
                            <TableCell align="right">{row.errors.toLocaleString()}</TableCell>
                            <TableCell align="right">{formatTokens(row.promptTokens)}</TableCell>
                            <TableCell align="right">{formatTokens(row.cachedTokens)}</TableCell>
                            <TableCell align="right">{formatTokens(row.completionTokens)}</TableCell>
                            <TableCell align="right">{formatCost(row.costUsd)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

function RunsTable({runs}: {runs: UsageRun[]}): ReactElement {
    if (runs.length === 0) {
        return <Typography variant="body2" color="text.secondary">No batch runs have been recorded yet.</Typography>;
    }
    return (
        <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader aria-label="recorded runs">
                <TableHead>
                    <TableRow>
                        <TableCell>Finished</TableCell>
                        <TableCell>Job</TableCell>
                        <TableCell>Subject</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Minutes</TableCell>
                        <TableCell align="right">Calls</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Cost</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {runs.map((run, index) => {
                        const totals = runTotals(run);
                        return (
                            <TableRow key={`${run.recordedAt ?? ''}-${run.jobExecutionId ?? index}`}>
                                <TableCell>{formatDayTime(run.recordedAt) ?? ''}</TableCell>
                                <TableCell>{JOB_LABELS[run.job ?? ''] ?? run.job ?? ''}</TableCell>
                                <TableCell>{subject(run)}</TableCell>
                                <TableCell>{run.status ?? ''}</TableCell>
                                <TableCell align="right">{run.minutes === undefined ? '' : run.minutes.toFixed(1)}</TableCell>
                                <TableCell align="right">{totals.calls.toLocaleString()}</TableCell>
                                <TableCell align="right">{formatTokens(totals.promptTokens)}</TableCell>
                                <TableCell align="right">{formatTokens(totals.completionTokens)}</TableCell>
                                <TableCell align="right">{formatCost(run.costUsd)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function LlmUsagePage(): ReactElement | null {
    const navigate = useNavigate();
    const isAuthorized = authService.isSuperUser();
    const {report, error, loading, refreshing, loadedAt, refresh} = useLlmUsage(isAuthorized);

    useEffect(() => {
        if (!isAuthorized) {
            navigate('/react/workspace');
        }
    }, [isAuthorized, navigate]);

    if (!isAuthorized) return null;

    const runs = report?.runs ?? [];
    const sinceStartup = report?.sinceStartup ?? [];
    const sinceStartupCost = sinceStartup.reduce((sum, row) => sum + (row.costUsd ?? 0), 0);
    const unpriced = sinceStartup.filter(row => row.costUsd === null).length;
    const recordingSince = report?.spend.recordingSince ? formatDayTime(report.spend.recordingSince) : null;

    return (
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, overflow: 'auto', height: '100vh',
            bgcolor: 'background.default', gridColumn: '1 / -1' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: 240 }}>
                    <Typography variant="h6">LLM Usage</Typography>
                    {report && (
                        <Typography variant="body2" color="text.secondary">{describeProvider(report.provider)}</Typography>
                    )}
                </Box>
                {loadedAt && (
                    <Typography variant="caption" color="text.secondary">
                        Updated {loadedAt.toLocaleTimeString()}
                    </Typography>
                )}
                <Button size="small" startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                    disabled={refreshing} onClick={(): void => { void refresh(); }}>
                    Refresh
                </Button>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            {loading && !report && <CircularProgress size={24} aria-label="Loading LLM usage" />}

            {report && (
                <Box aria-busy={refreshing} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                        <StatCard label="This month (UTC)" value={formatCost(report.spend.thisMonthUsd)}
                            detail="Every call: batch, chat, agents and search" />
                        <StatCard label="Last 30 days" value={formatCost(report.spend.last30DaysUsd)}
                            detail="Every call: batch, chat, agents and search" />
                        <StatCard label="All time" value={formatCost(report.spend.allUsd)}
                            detail={recordingSince ? `Since ${recordingSince}` : 'Since this server started'} />
                        <StatCard label="Since server start" value={formatCost(sinceStartupCost)}
                            detail={`Since ${formatDayTime(report.startedAt) ?? ''}`
                                + (unpriced > 0 ? `; ${unpriced} model${unpriced === 1 ? '' : 's'} not priced` : '')} />
                    </Box>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>Since server start</Typography>
                        <ModelUsageTable rows={sinceStartup} />
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1">Recorded batch runs</Typography>
                        <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                            Newest first{report.logFile ? `, from ${report.logFile}` : ''}
                            {report.runsInFile > runs.length ? `; showing the latest ${runs.length} of ${report.runsInFile}` : ''}.
                            Costs are at list price. A run is also charged for anything else that called a model while
                            it ran, so runs are not added up; the totals above count each call once.
                        </Typography>
                        <RunsTable runs={runs} />
                    </Paper>

                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>List prices, US dollars per million tokens</Typography>
                        <TableContainer>
                            <Table size="small" aria-label="model prices">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Model</TableCell>
                                        <TableCell align="right">Input</TableCell>
                                        <TableCell align="right">Cached input</TableCell>
                                        <TableCell align="right">Output</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {report.prices.map(price => (
                                        <TableRow key={price.model}>
                                            <TableCell>{price.model}</TableCell>
                                            <TableCell align="right">{price.inputPerMillion.toFixed(3)}</TableCell>
                                            <TableCell align="right">{price.cachedPerMillion.toFixed(3)}</TableCell>
                                            <TableCell align="right">{price.outputPerMillion.toFixed(3)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}
        </Box>
    );
}
